import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import {
  COMPANY_QUALITIES_NAMES,
  COMPETITIVE_ANALYSIS_NAMES,
  COMPETITORS_COMPARISON_NAMES,
  FEATURES_FOR_COMPARISON_NAMES,
  FEATURES_LIST_NAMES,
  KEY_COMPETITORS_NAMES,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/CompetitiveAnalysis/utils/form';
import {
  B2B_NAMES,
  B2C_NAMES,
  CONSUMER_NEEDS_NAMES,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/ConsumerNeeds/utils/form';
import {
  AVERAGE_PRICES_CHART_NAMES,
  CONSUMER_DETAILS_NAMES,
  CURRENT_MARKET_STRUCTURE_NAMES,
  FORECASTED_CONSUMPTION_CHART_NAMES,
  MARKET_ANALYSIS_OVERVIEW_NAMES,
  MARKET_TRENDS_FIELD_NAMES,
  REGION_PERIODS_NAMES,
  REGIONS_PRODUCT_NAMES,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/utils/form';

import { ul } from '../helpers';

const YEARS_ROW_INSTRUCTIONS = {
  name: 'Значення',
  instruction: 'Укажіть значення ринку, переконайтеся, що воно точне й зрозуміле.',
};

const CONSUMPTION_TABLE_DATA_INSTRUCTIONS = {
  [REGION_PERIODS_NAMES.Y1]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y2]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y3]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y4]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y5]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y6]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.REGION]: {
    name: "Назва",
    instruction: 'Укажіть точну назву ринку, переконайтеся, що вона є коректною й зрозумілою.',
    exampleOne: 'США',
    exampleTwo: 'Європа',
  },
};

const FORECASTED_CONSUMPTION_CHART_INSTRUCTIONS = {
  [FORECASTED_CONSUMPTION_CHART_NAMES.TITLE]: {
    name: 'Прогноз споживання',
    instruction: 'Надайте чітку й лаконічну назву для графіка, що точно відображає його зміст.',
    exampleOne: 'Європейський ринок фінансових послуг',
    exampleTwo: 'Американський ринок сільськогосподарської техніки',
  },
  [FORECASTED_CONSUMPTION_CHART_NAMES.REGION]: {
    name: '',
    instruction: '',
    exampleOne: '',
    exampleTwo: '',
  },
  [FORECASTED_CONSUMPTION_CHART_NAMES.UNITS]: {
    name: '',
    instruction: '',
    exampleOne: '',
    exampleTwo: '',
  },
  [FORECASTED_CONSUMPTION_CHART_NAMES.TABLE_ROWS]: CONSUMPTION_TABLE_DATA_INSTRUCTIONS,
  [FORECASTED_CONSUMPTION_CHART_NAMES.SOURCE]: {
    name: 'Джерело даних',
    instruction: 'Чітко вкажіть надійне джерело або метод отримання інформації.',
    exampleOne: 'Eurostat',
    exampleTwo: 'Statista',
  },
};

const CONSUMER_DETAILS_INSTRUCTIONS = {
  [CONSUMER_DETAILS_NAMES.CONSUMER]: {
    name: 'Споживач',
    instruction: 'Укажіть точну назву сегмента, переконайтеся, що вона коректна й зрозуміла.',
    exampleOne: 'США\n' + 'Європа\n' + 'Азія',
    exampleTwo: 'Фермери\n' + 'Агропідприємства',
  },
  [CONSUMER_DETAILS_NAMES.PERCENTAGE_SHARE]: {
    name: 'Відсоткова частка',
    instruction:
      'Укажіть частку кожного сегмента. Надайте відсоток для кожного сегмента й переконайтеся, що сума дорівнює 100%.',
    exampleOne: '',
    exampleTwo: '',
  },
  [CONSUMER_DETAILS_NAMES.PHYSICAL_SHARE]: {
    name: 'Фізична спільна публікація',
    instruction: 'Укажіть частку кожного сегмента у фізичних одиницях. Надайте чіткі, точні показники.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const MARKET_TRENDS_INSTRUCTIONS = {
  [MARKET_TRENDS_FIELD_NAMES.TREND_NAME]: {
    name: 'Назва тренду',
    instruction:
      'Визначте основні ринкові тренди. Переконайтеся, що вони релевантні вашій галузі та відображають поточну динаміку ринку.',
    exampleOne: 'Зосередження на сталому розвитку',
    exampleTwo: 'Спеціалізація в нішевих галузях',
  },
  [MARKET_TRENDS_FIELD_NAMES.TREND_CHARACTERISTICS]: {
    name: 'Аналіз трендів',
    instruction:
      'Надайте огляд поточних ринкових трендів у вашій галузі. Визначте та опишіть ключові зрушення, вподобання або моделі поведінки, що формують ринок. Підкресліть останні події, які впливають на вибір споживачів, практики в галузі чи технологічний розвиток.',
    exampleOne:
      'Виробники розробляють екологічно чисту техніку зі зменшеними викидами. Крім того, зростає попит на техніку, що підтримує сталі сільськогосподарські практики, такі як ґрунтозахисна обробка й органічне землеробство.',
    exampleTwo:
      'Зростає тренд до спеціалізації в нішевих галузях, таких як юридичні, медичні, технічні та маркетингові переклади, оскільки клієнти віддають перевагу перекладачам з експертизою в конкретних предметних сферах.',
  },
};

const CURRENT_MARKET_STRUCTURE_INSTRUCTIONS = {
  [CURRENT_MARKET_STRUCTURE_NAMES.TITLE]: {
    name: 'Назва діаграми',
    instruction: 'Надайте чітку й лаконічну назву для графіка, що точно відображає його зміст.',
    exampleOne: 'Географічна структура ринку',
    exampleTwo: 'Структура ринку за споживачами',
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER]: {
    name: '',
    instruction: 'Укажіть точну назву сегмента, переконайтеся, що вона коректна й зрозуміла.',
    exampleOne: `USA
    Europe
    Asia`,
    exampleTwo: `Фермери
    Агропідприємства`,
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.DATE]: {
    name: '',
    instruction: 'Укажіть актуальну дату для цієї інформації, включно з місяцем і роком.',
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.UNITS]: {
    name: '',
    instruction: '',
    exampleOne: '',
    exampleTwo: '',
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER_DETAILS]: CONSUMER_DETAILS_INSTRUCTIONS,
  [CURRENT_MARKET_STRUCTURE_NAMES.SOURCE]: {
    name: 'Джерело даних',
    instruction: 'Чітко вкажіть надійне джерело або метод отримання інформації.',
    exampleOne: 'Eurostat',
    exampleTwo: 'Statista',
  },
};

const PRODUCT_ROW_INSTRUCTIONS = {
  name: 'Продукт/Послуга',
  instruction:
    'Укажіть точну назву продукту або послуги. Переконайтеся, що вона коректно й однозначно відображає пропозицію та відповідає офіційним правилам найменування.',
  exampleOne: 'Гусеничні трактори',
  exampleTwo: 'Інструменти перекладу на базі штучного інтелекту',
};

const AVERAGE_PRICES_TABLE_DATA_INSTRUCTIONS = {
  [REGIONS_PRODUCT_NAMES.PRODUCT]: PRODUCT_ROW_INSTRUCTIONS,
};

const MARKET_OVERVIEW_INSTRUCTIONS = {
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MAIN_MARKET_OVERVIEW]: {
    name: 'Сегментація ринку',
    instruction:
      'Сформулюйте визначення ринку, який розглядаєте. Опишіть його ключові характеристики, що визначають його природу. Розкрийте ринкові бар’єри та виклики, які можуть впливати на вихід на ринок або операційну діяльність. Надайте стислий огляд сегментів ринку, акцентуючи увагу на географічних та галузевих категоріях, типах клієнтів тощо.',
    exampleOne:
      'Ринок, що розглядається, — це сектор сільськогосподарської техніки. Ринок сегментується за типом техніки: трактори, ґрунтообробна, посівна й збиральна техніка. Найбільшим сегментом є трактори, оскільки це універсальна сільськогосподарська техніка.',
    exampleTwo:
      'Ринок мовних послуг обслуговує клієнтів і перекладачів по всьому світу. Хоча серйозних бар’єрів для входу немає, конкуренція дуже висока. Сегменти включають письмовий переклад, усний переклад, субтитрування та озвучування, при цьому домінує письмовий переклад.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.SERVICES_PRODUCTS_SUMMARY]: {
    name: 'Огляд продукту',
    instruction:
      'Опишіть основні послуги або продукти, що пропонуються на вашому цільовому ринку. Які їхні ключові характеристики, основне призначення та цінові стратегії? Як ці фактори впливають на попит і конкуренцію на ринку?',
    exampleOne:
      'Пропозиція на ринку включає трактори класу A та класу B для різних потреб клієнтів. Трактори класу A мають меншу вагу, щоб зменшити тиск на ґрунт, тоді як трактори класу B характеризуються високою надійністю та прохідністю. Стартова ціна — від $25,000.',
    exampleTwo:
      'Пропозиція на ринку включає послуги письмового, машинного та AI-перекладу з різною вартістю залежно від складності проєкту. Унікальними перевагами є точність, ефективність і економічність, що відповідає різноманітним потребам клієнтів.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.CONSUMERS_DESCRIPTION]: {
    name: 'Демографія споживачів',
    instruction:
      'Узагальніть демографічні характеристики, поведінку та вподобання споживачів. Використайте дані для опису груп, територій і моделей попиту.',
    exampleOne:
      'Споживачами є фермери в усьому світі, переважно в регіонах із важкими або вологими ґрунтами. Вони шукають високопродуктивні трактори з низьким тиском на ґрунт. Попит змінюється залежно від посівних і збиральних сезонів.',
    exampleTwo:
      'Споживачами є як фізичні особи, так і корпорації. Попит поширений глобально, з високою концентрацією у міжнародних ділових центрах і регіонах із великою кількістю мігрантів. Вподобання схиляються до ефективних і надійних рішень для комунікації в різних галузях.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_COMPETITORS]: {
    name: 'Визначення конкурентів',
    instruction:
      'Визначте конкурентів на ринку за кількістю, виділяючи лідерів та їхнє позиціонування. Проаналізуйте сильні та слабкі сторони, а також частку ринку, щоб зрозуміти конкурентне середовище.',
    exampleOne:
      'Ринок складається приблизно з 2000 виробників тракторів, серед яких John Deere лідирує з часткою ринку 20%. Позиціонування John Deere підкреслює надійність та інновації. Конкуренти різняться за розміром та спеціалізацією, кожен з яких має унікальні сильні та слабкі сторони.',
    exampleTwo:
      'На ринку є 15 платформ мовних послуг, лідерами яких є T1 та STL. T1 займає найбільшу частку ринку завдяки своїй розгалуженій мережі перекладачів та зручному інтерфейсу. STL диференціюється завдяки інтеграції передових технологій та персоналізованому обслуговуванню клієнтів.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.ACTUAL_AND_FORECASTED_CONSUMPTION]: {
    name: 'Аналіз споживання',
    instruction:
      'Опишіть тенденції споживання, використовуючи статистику. Наведіть фактичні дані та прогнозовані цифри, виділивши траєкторії зростання та динаміку ринку.',
    exampleOne:
      'У 2022 році на ринку було продано 1 мільйон гусеничних тракторів, з очікуваним річним темпом зростання 5-7%. Це зростання відображає посилення механізації в сільському господарстві та зростання попиту на надійні трактори з низьким впливом на навколишнє середовище.',
    exampleTwo:
      'Згідно зі звітом консалтингової компанії A, загальний обсяг ринку мовних послуг у 2024 році оцінюється в 54 мільярди доларів. Прогнозується, що ринок мовних послуг зростатиме зі середньорічним темпом зростання 17% і досягне 78 мільярдів доларів до 2027 року. Траєкторії зростання показують стабільне зростання попиту на мовні послуги.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.FORECASTED_CONSUMPTION_CHART]: FORECASTED_CONSUMPTION_CHART_INSTRUCTIONS,
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_SHARE]: {
    name: 'Аналітика частки ринку',
    instruction:
      'Проаналізуйте дані про продажі, щоб визначити поточну частку ринку. Підкресліть вплив бренду через лояльність клієнтів, інноваційність і стратегії розширення ринку.',
    exampleOne:
      'Поточна частка ринку Компанії становить 0,02%. Компанія займає сильні позиції завдяки своїм інноваційним гусеничним тракторам. Завдяки передовим технологіям і вигідному співвідношенню ціни та якості Компанія здобула високу лояльність клієнтів.',
    exampleTwo:
      'Компанія утримує 15% ринку в секторі мовних послуг. Вона є конкурентоспроможною, має сильне впізнавання бренду й лояльну клієнтську базу. Інноваційні технології та різноманітні платіжні опції надають Компанії додаткову перевагу.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_TRENDS]: MARKET_TRENDS_INSTRUCTIONS,
  [MARKET_ANALYSIS_OVERVIEW_NAMES.SALES_METHOD]: {
    name: 'Стратегія продажів',
    instruction:
      'Опишіть канали збуту продукту/послуги. Розкрийте використання онлайн-платформ, роздрібних партнерств і стратегій прямих продажів для ефективного охоплення цільових клієнтів.',
    exampleOne:
      'Типові канали збуту включають сайт компанії, e-commerce-портали, партнерства з магазинами сільськогосподарської техніки та прямі продажі через представників компанії.',
    exampleTwo:
      'Канали збуту базуються на мультиканальному підході, поєднуючи онлайн-платформу, стратегічні партнерства з мовними школами та прямі продажі корпоративним клієнтам через персоналізовані консультації та демо, щоб оптимізувати ефективність комунікацій.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_STRUCTURE_DESCRIPTION]: {
    name: 'Огляд структури ринку',
    instruction: 'Прокоментуйте графік нижче, визначте ключові сегменти.',
    exampleOne:
      'На ринку сільськогосподарської техніки ключові сегменти включають: Конкурентів: великих виробників, таких як John Deere, Caterpillar та CNH Industrial. Споживчі сегменти: фермери, аграрні кооперативи й агрохолдинги.',
    exampleTwo:
      'Ключові сегменти можуть включати: фриланс-перекладачів, перекладацькі агенції, провайдерів мовних технологій, внутрішні перекладацькі відділи (корпоративні), компанії машинного перекладу.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.CURRENT_MARKET_STRUCTURE]: CURRENT_MARKET_STRUCTURE_INSTRUCTIONS,
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_GROWTH_FACTORS]: {
    name: 'Фактори росту',
    instruction:
      'Спершу визначте ключові фактори, що, як очікується, сприятимуть зростанню вашого ринку. Це можуть бути макроекономічні тренди, технологічні досягнення, регуляторні зміни або зрушення в уподобаннях споживачів. Надайте стислий, але вичерпний огляд зовнішніх чинників, які позитивно впливатимуть на розширення ринку.',
    exampleOne:
      'Ключовими драйверами зростання ринку є екологічна сталисть, упровадження передових технологій і сприятлива економічна кон’юнктура. Ці тренди стимулюють попит на високотехнологічну, екологічно дружню сільськогосподарську техніку.',
    exampleTwo:
      'Зростанню ринку сприятиме подальший перехід до віддаленої роботи та віртуальної співпраці, прискорений пандемією COVID-19, що підвищує попит на сервіси віддаленого усного перекладу й онлайн-платформи для письмового перекладу.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.TECHNOLOGY_IMPACT]: {
    name: 'Вплив технологій',
    instruction:
      'Оцініть вплив технологічних змін на галузь. Підкресліть можливості для інновацій, підвищення ефективності та трансформації наявних бізнес-моделей.',
    exampleOne:
      'Галузь сільськогосподарської техніки має значний потенціал для інновацій завдяки технологічному прогресу. Автоматизація, точне землеробство й аналітика даних підвищують ефективність агровиробництва. Робототехніка дає змогу зменшити залежність від ручної праці.',
    exampleTwo:
      'Галузь мовних послуг активно використовує досягнення в галузі ШІ та машинного навчання для підвищення точності й ефективності перекладу. Окрім цього, відбувається розробка спеціалізованих інструментів, адаптованих під конкретні галузі або мови.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.PRICE_STRATEGIES]: {
    name: 'Стратегії ціноутворення',
    instruction:
      'Проаналізуйте цінові стратегії конкурентів. Оцініть такі фактори, як моделі ціноутворення, знижки та ціннісні пропозиції. Адаптуйте власну цінову стратегію так, щоб залишатися конкурентоспроможними та водночас зберігати рентабельність.',
    exampleOne:
      'Конкуренти використовують різні цінові стратегії, такі як ціноутворення на основі цінності, стратегія проникнення на ринок і націнка до собівартості. Можуть надаватися знижки за оптові закупівлі або сезонні акції. Власну цінову політику слід адаптувати до ціннісних пропозицій, балансуючи між конкурентоспроможністю й прибутковістю.',
    exampleTwo:
      'Конкуренти застосовують різні моделі ціноутворення: погодинні ставки для фрилансерів, оплату за слово для агенцій, абонплату для постачальників мовних технологій. Розуміння цих підходів допомагає формувати конкурентну цінову політику.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_TITLE]: {
    name: 'Огляд цін',
    instruction: 'Надайте чітку й лаконічну назву для графіка, що точно відображає його зміст.',
    exampleOne: 'Ціни на гусеничні трактори',
    exampleTwo: 'Ціни на інструменти перекладу на базі штучного інтелекту',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_DATE]: {
    name: '',
    instruction: 'Укажіть актуальну дату для цієї інформації, включно з місяцем і роком.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CHART]: {
    [AVERAGE_PRICES_CHART_NAMES.AVERAGE_PRICES_REGIONS_ROWS]: AVERAGE_PRICES_TABLE_DATA_INSTRUCTIONS,
  },
};

const KEY_COMPETITORS_INSTRUCTIONS = {
  [KEY_COMPETITORS_NAMES.COMPETITOR]: {
    name: "Назва",
    instruction: 'Укажіть повну офіційну назву компанії.',
    exampleOne: 'AgroTech Innovations',
    exampleTwo: 'TransLink',
  },
  [KEY_COMPETITORS_NAMES.FILE]: {
    name: '',
    instruction:
      'Додайте логотип компанії. Переконайтеся, що логотип має високу роздільну здатність, відповідний розмір і прозорий фон.',
    exampleOne: '',
    exampleTwo: '',
  },
  [KEY_COMPETITORS_NAMES.WEBSITE]: {
    name: 'Сайт',
    instruction: 'Укажіть повну вебадресу компанії.',
    exampleOne: 'agro.tech.innovations.com',
    exampleTwo: 'translink.com',
  },
  [KEY_COMPETITORS_NAMES.LOCATION]: {
    name: 'Місцезнаходження',
    instruction: 'Укажіть місто й країну, де компанію офіційно зареєстровано.',
    exampleOne: 'вул. Сен 134, Меріон, Північна Кароліна 28752, США',
    exampleTwo: 'Портленд, США',
  },
  [KEY_COMPETITORS_NAMES.DESCRIPTION]: {
    name: 'Короткий опис продукту',
    instruction:
      'Опишіть ключові характеристики продукту. Підкресліть інноваційні аспекти чи технологічні переваги, які вирізняють його на ринку. Чітко розкрийте функціональність, специфікації та елементи дизайну, що формують його унікальність. Поясніть, як ці характеристики закривають конкретні потреби або болі цільового ринку, акцентуючи на ціннісній пропозиції продукту.',
    exampleOne:
      'Компанія пропонує різноманітну сільськогосподарську техніку, зокрема трактори, комбайни, сівалки та обприскувачі. Основний продукт — потужні трактори, що використовують біопаливо як основне джерело енергії. Вони створені для максимальної продуктивності й ефективності фермерів.',
    exampleTwo:
      'Конкурент пропонує сучасну платформу мовних послуг. Ключові можливості включають інструменти для співпраці в реальному часі та доступ до широкої мережі професійних перекладачів.',
  },
  [KEY_COMPETITORS_NAMES.POSITIONING]: {
    name: 'Позиціонування',
    instruction:
      'Надайте огляд ринкового позиціонування конкурентів. Опишіть їхнє поточне становище на ринку, підкреслюючи фактори, такі як впізнаваність бренду й загальний вплив на ринок.',
    exampleOne: 'Компанія позиціонує себе як виробника, що просуває екологічні та сталі сільськогосподарські практики.',
    exampleTwo:
      'Компанія посідає сильні позиції на ринку й відома завдяки усталеному бренду та розгалуженій мережі перекладачів. Вона фокусується на безперервній комунікації, культурному розумінні та ефективній локалізації, пропонуючи індивідуалізовані перекладацькі послуги, які відповідають специфічним потребам клієнтів.',
  },
  [KEY_COMPETITORS_NAMES.SALES_CHANNELS]: {
    name: 'Канали продажів',
    instruction:
      'Надайте огляд каналів збуту, які використовують ключові конкуренти. Це може включати їхні дистриб’юторські мережі, роздрібні партнерства, онлайн-платформи чи інші канали взаємодії з клієнтами.',
    exampleOne: 'Включають дилерські мережі, онлайн-платформи та прямі продажі великим фермерським господарствам.',
    exampleTwo:
      'Компанія реалізує свої продукти через онлайн-платформи, власний вебсайт, галузеві організації та таргетовані email-кампанії.',
  },
  [KEY_COMPETITORS_NAMES.PRICING_POLICY]: {
    name: 'Цінова політика',
    instruction:
      'Надайте стислий огляд цінової політики ключових конкурентів. Опишіть стратегії ціноутворення, які вони використовують — націнка до собівартості, ціноутворення на основі цінності, конкурентне ціноутворення чи їхні комбінації. Підкресліть унікальні риси їхнього підходу до ціноутворення, що вирізняють їх на ринку.',
    exampleOne:
      'Компанія застосовує конкурентну цінову стратегію, роблячи акцент на співвідношенні ціни та цінності, водночас підкреслюючи унікальні характеристики й переваги своєї продукції.',
    exampleTwo:
      'Конкурент використовує динамічну цінову стратегію, коригуючи ціни залежно від попиту та складності перекладацьких завдань.',
  },
  [KEY_COMPETITORS_NAMES.ANNUAL_SALES_VOLUME]: {
    name: 'Річний обсяг продажів',
    instruction:
      'Укажіть річний обсяг продажів компанії, надавши загальний дохід від реалізації за останній фінансовий рік.',
    exampleOne: '25 000 000 USD',
    exampleTwo: '7 000 000 USD',
  },
  [KEY_COMPETITORS_NAMES.MARKET_SHARE]: {
    name: 'Частка ринку',
    instruction: 'Проаналізуйте ринкові дані й визначте частку компанії на ринку.',
    exampleOne: '0.02%',
    exampleTwo: '13%',
  },
  [KEY_COMPETITORS_NAMES.CUSTOMER_LOYALTY_LEVEL]: {
    name: 'Рівень лояльності клієнтів',
    instruction: 'Стисло вкажіть рівень лояльності клієнтів, зазначивши, чи він високий, середній чи низький.',
    exampleOne: 'Високий',
    exampleTwo: 'Середній',
  },
};

const COMPETITORS_STRENGTHS = {
  [COMPANY_QUALITIES_NAMES.STRENGTH]: {
    name: 'Сильні сторони',
    instruction:
      'Визначте ключові компетенції та ресурси, що забезпечують конкурентну перевагу на ринку. Підкресліть спеціалізовані навички, унікальні можливості та репутацію бренду.',
    exampleOne: ul([
      'Інноваційне використання технологій біопалива у виробництві тракторів',
      'Виробничий майданчик розташований поруч із залізничною розв’язкою',
      'Сертифікат відповідності екологічним вимогам',
    ]),
    exampleTwo: ul([
      'Розгалужена мережа професійних перекладачів',
      'Безпечні платіжні системи',
      'Інструменти для співпраці в режимі реального часу',
    ]),
  },
};
const COMPETITORS_WEAKNESSES = {
  [COMPANY_QUALITIES_NAMES.WEAKNESSES]: {
    name: 'Слабкі сторони',
    instruction:
      'Визначте внутрішні обмеження та виклики компанії. Відверто опишіть сфери, які потребують покращення: процеси, ресурси, навички. Зазначте прогалини у відповідності до галузевих стандартів, операційну неефективність та їхній вплив на результати роботи й конкурентоспроможність.',
    exampleOne: ul([
      'Обмежена лінійка продуктів',
      'Вищі виробничі витрати, пов’язані з екологічно дружніми технологіями',
    ]),
    exampleTwo: ul([
      'Застарілий інтерфейс платформи',
      'Вищі ціни порівняно з конкурентами',
      'Обмежена кількість інструментів пошуку перекладачів',
    ]),
  },
};

const COMPETITORS_QUALITIES_INSTRUCTIONS = {
  [COMPANY_QUALITIES_NAMES.NAME]: {
    name: "Назва",
    instruction: 'Укажіть повну офіційну назву компанії.',
    exampleOne: 'AgroTech Innovations',
    exampleTwo: 'TransLink',
  },
  [COMPANY_QUALITIES_NAMES.FILE]: {
    name: 'Логотип',
    instruction:
      'Додайте логотип компанії. Переконайтеся, що логотип має високу роздільну здатність, відповідний розмір і прозорий фон.',
    exampleOne: '',
    exampleTwo: '',
  },
  [COMPANY_QUALITIES_NAMES.STRENGTH]: COMPETITORS_STRENGTHS,
  [COMPANY_QUALITIES_NAMES.WEAKNESSES]: COMPETITORS_WEAKNESSES,
};

const COMPARISON_ITEM_INSTRUCTIONS = {
  [COMPETITORS_COMPARISON_NAMES.FEATURES_FOR_COMPARISON]: {
    [FEATURES_FOR_COMPARISON_NAMES.FEATURE]: {
      name: 'Функція',
      instruction:
        'Опишіть відмінні характеристики й переваги, які вирізняють ваш продукт чи послугу. Чітко поясніть, що саме робить вашу пропозицію кращою.',
      exampleOne: 'Кінська сила',
      exampleTwo: 'Середній час виконання',
    },
  },
  [FEATURES_LIST_NAMES.NAME]: {
    name: "Назва",
    instruction: 'Укажіть повну офіційну назву компанії.',
    exampleOne: 'John Deere',
    exampleTwo: 'LinguaWorld',
  },
  [FEATURES_LIST_NAMES.FILE]: {
    name: '',
    instruction:
      'Додайте логотип компанії. Переконайтеся, що логотип має високу роздільну здатність, відповідний розмір і прозорий фон.',
    exampleOne: '',
    exampleTwo: '',
  },
  [FEATURES_LIST_NAMES.FEATURE]: {
    name: 'Значення функції ',
    instruction: `Кількісно опишіть цінність вашого продукту чи послуги для клієнтів, підкресливши вимірювані результати, економію коштів або підвищення ефективності.`,
    exampleOne: '200',
    exampleTwo: '20 годин',
  },
  [COMPETITORS_COMPARISON_NAMES.COMPETITOR_FEATURES_LIST]: {
    [FEATURES_LIST_NAMES.FILE]: {
      name: '',
      instruction:
        'Додайте логотип компанії. Переконайтеся, що логотип має високу роздільну здатність, відповідний розмір і прозорий фон.',
      exampleOne: '',
      exampleTwo: '',
    },
    [FEATURES_LIST_NAMES.NAME]: {
      name: "Назва",
      instruction: 'Укажіть повну офіційну назву компанії.',
      exampleOne: 'John Deere',
      exampleTwo: 'LinguaWorld',
    },
    [FEATURES_LIST_NAMES.FEATURE]: {
      name: 'Значення функції ',
      instruction: `Кількісно опишіть цінність вашого продукту чи послуги для клієнтів, підкресливши вимірювані результати, економію коштів або підвищення ефективності.`,
      exampleOne: '200',
      exampleTwo: '20 годин',
    },
  },
};

const COMPETITIVE_ANALYSIS_INSTRUCTIONS = {
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS]: {
    name: 'Конкурентний аналіз',
    instruction:
      'Складіть список прямих і непрямих конкурентів, пояснивши, чому вони є релевантними. Підкресліть схожість у продуктах/послугах, цільових ринках і каналах збуту, акцентуючи на конкурентних перевагах і ринковому позиціонуванні.',
    exampleOne:
      'Прямі конкуренти AgroTech і Harvest Dynamics пропонують подібні важкі трактори. Непрямі конкуренти включають GHY, яка пропонує двоколісні трактори й альтернативні рішення. Усі вони орієнтуються на тих самих фермерів і використовують подібні канали дистрибуції.',
    exampleTwo:
      'До прямих конкурентів належать TransLink і LinHub, які пропонують широкий спектр мовних послуг, використовують технології машинного перекладу й фокусуються на клієнтах–фізичних особах. Непрямі конкуренти — це платформи для пошуку фрилансерів, які також надають мовні послуги.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.ATTRACTING_COMPETITORS_CUSTOMERS]: {
    name: 'Залучення клієнтів',
    instruction:
      'Опишіть стратегії залучення клієнтів конкурентів. Підкресліть унікальну ціннісну пропозицію, цінові переваги й маркетингові активності, які спонукають клієнтів змінити постачальника, та допомагають утримувати їх надалі.',
    exampleOne:
      'Компанія пропонуватиме конкурентні ціни, стартові знижки та програми trade-in. Також вона використовуватиме digital-маркетинг, участь у галузевих заходах і стратегічні партнерства.',
    exampleTwo:
      'Щоб залучити клієнтів конкурентів, Компанія реалізує масштабну PR-кампанію в соціальних мережах і співпрацюватиме з блогерами. Підвищення впізнаваності бренду посилить позиції компанії та приверне нових клієнтів завдяки зростанню уваги до неї.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.COMPARISON_ITEM]: COMPARISON_ITEM_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.KEY_COMPETITORS]: KEY_COMPETITORS_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES]: COMPETITORS_QUALITIES_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.YOUR_COMPANY_QUALITIES]: COMPETITORS_QUALITIES_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_DIFFERENCE]: {
    name: 'Диференціація продукту',
    instruction:
      'Порівняйте продукти/послуги конкурентів за якістю й характеристиками. Підкресліть сильні сторони, такі як довговічність, продуктивність або унікальний функціонал. Акцентуйте на перевагах ваших рішень, щоб чітко відрізнятися від конкурентів.',
    exampleOne:
      'Конкуренти пропонують різні типи тракторів із власними унікальними особливостями. Деякі роблять акцент на екологічних біопаливних двигунах або специфічних матеріалах. Трактори Компанії вирізняються надійною конструкцією, що забезпечує стабільну роботу в складних умовах і на важких ґрунтах.',
    exampleTwo:
      'Конкуренти мають сильну впізнаваність бренду й значні рекламні бюджети, що забезпечує широке охоплення аудиторії. Платформа Компанії відрізняється інтегрованими AI-технологіями, які підвищують точність і швидкість перекладу, забезпечуючи кращу ефективність порівняно з конкурентами.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.PURCHASING_PRODUCTS]: {
    name: 'Мотивація клієнтів',
    instruction:
      'Проаналізуйте, чому клієнти обирають продукти конкурентів. Врахуйте ціну, репутацію бренду, функціонал і рівень сервісу. Визначте напрями покращення, щоб краще відповідати очікуванням і потребам вашої цільової аудиторії.',
    exampleOne:
      'Клієнти купують продукцію конкурентів завдяки відомості бренду, конкурентним цінам і широкому функціоналу. Щоб краще задовольняти потреби клієнтів, варто вдосконалити цінову стратегію, посилити впізнаваність бренду та запропонувати унікальні характеристики продукту.',
    exampleTwo:
      'Клієнти можуть обирати конкурентів через нижчі ціни й усталену репутацію бренду. Для ефективної конкуренції Компанії необхідно посилювати ціннісну пропозицію, пропонувати конкурентні ціни й підвищувати якість клієнтської підтримки.',
  },
  // [COMPETITIVE_ANALYSIS_NAMES.PRODUCT]: {
  //   name: '',
  //   instruction: '',
  //   exampleOne: '',
  //   exampleTwo: '',
  // },
  [COMPETITIVE_ANALYSIS_NAMES.FUTURE_EXPECTATIONS]: {
    name: 'Майбутня конкуренція',
    instruction:
      'Оцініть потенційних нових гравців на ринку. Врахуйте привабливість галузі, бар’єри входу та наявні ринкові ніші. Підкресліть ризики посилення конкуренції, цінових воєн і можливої втрати частки ринку.',
    exampleOne:
      'Технологічний прогрес і зміна споживчих переваг формуватимуть майбутню конкуренцію. Глобалізація та зростання ринків можуть призвести до посилення конкуренції. Сильні позиції й впізнаваність бренду усталених гравців створюють бар’єри для входу нових компаній.',
    exampleTwo:
      'Потенційні нові гравці стикаються з низькими бар’єрами входу, зважаючи на глобальний характер ринку, але водночас із високою конкуренцією. Ризик полягає в складності вибудови власного бренду на тлі відомих гравців та у потребі пропонувати інноваційні рішення для закриття ринкових прогалин.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.BARRIERS]: {
    name: "Бар'єри входу",
    instruction:
      'Визначте бар’єри для входу на ринок. Опишіть такі фактори, як значні капітальні інвестиції, регуляторні вимоги, лояльність клієнтів до наявних брендів і ефект масштабу, які стримують появу нових гравців.',
    exampleOne:
      'Умови входу на ринок включають значні капітальні витрати на R&D і організацію виробництва. Висока конкуренція й сильна впізнаваність брендів існуючих гравців формують суттєві бар’єри для нових компаній.',
    exampleTwo:
      'Нові гравці стикаються з низкою викликів, включно з великими стартовими інвестиціями в технологічну інфраструктуру та необхідністю побудови надійної мережі перекладачів.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.CHANGING_OF_CURRENT_MARKET]: {
    name: 'Фактори, що змінюють ринок',
    instruction:
      ' Спрогнозуйте які фактори можуть потенційно змінити ринок. Оцініть вплив нових технологій, змін у вподобаннях споживачів і регуляторних нововведень. Підкресліть інновації, які можуть радикально змінити правила гри в галузі та загрожувати поточним гравцям.',
    exampleOne:
      'Поява повністю роботизованих тракторів із вбудованим AI є суттєвою загрозою для наявних виробників. Така інновація може змінити ринкову динаміку, скоротивши потребу в людській праці в агросекторі й поставивши під тиск традиційних виробників тракторів.',
    exampleTwo:
      'Розвиток AI-рішень для синхронного перекладу може суттєво скоротити попит на послуги людських перекладачів. Такі технології здатні змінити галузь, пропонуючи швидші й дешевші рішення, що змусить поточних гравців адаптуватися до нових реалій.',
  },
};

const CONSUMER_NEEDS_INSTRUCTIONS = {
  [CONSUMER_NEEDS_NAMES.B2C]: {
    [B2C_NAMES.GENDER]: {
      name: 'Стать',
      instruction:
        ' Вкажіть стать цільового споживача, зазначивши, чи це переважно чоловіки, жінки або гендерно-нейтральна аудиторія.',
      exampleOne: '',
      exampleTwo: '',
    },
    [B2C_NAMES.AGE]: {
      name: 'Вік',
      instruction:
        'Визначте віковий діапазон споживачів, спираючись на демографічні дані й результати маркетингових досліджень. Переконайтеся, що він коректно відображає цільову аудиторію.',
      exampleOne: '25-65',
      exampleTwo: '18-45',
    },
    [B2C_NAMES.ANNUAL_INCOME]: {
      name: 'Річний дохід',
      instruction:
        'Визначте рівень доходу споживачів, спираючись на ринкову демографію та показники купівельної спроможності. Переконайтеся, що він релевантний для сегментації цільового ринку.',
      exampleOne: '7 000 USD',
      exampleTwo: '5 000 USD',
    },
    [B2C_NAMES.PROFESSION]: {
      name: 'Професія',
      instruction:
        'Опишіть типові професії представників цільової аудиторії, спираючись на дослідження ринку й демографічний аналіз. Важливо, щоб вони відповідали вашим ключовим сегментам.',
      exampleOne: 'Фермери',
      exampleTwo: 'Студенти, працівники багатонаціональних корпорацій, академічнної та туристичної галузі',
    },
    [B2C_NAMES.PERSONAL_INTERESTS]: {
      name: 'Особисті інтереси',
      instruction:
        'Опишіть особисті інтереси цільової аудиторії. Використовуйте демографічні інсайти, щоб визначити хобі, вподобання та стиль життя, які впливають на їхні купівельні рішення.',
      exampleOne: 'Сільське господарство, техніка, технології, охорона навколишнього середовища.',
      exampleTwo: 'Цікавлюся іноземними культурами, технологіями та ефективною комунікацією',
    },
    [B2C_NAMES.PURCHASING_HABITS]: {
      name: 'Купівельні звички',
      instruction:
        'Проаналізуйте купівельні звички цільової аудиторії. На основі даних визначте частоту покупок, улюблені канали придбання та чинники, що впливають на ухвалення рішення.',
      exampleOne:
        'Зазвичай клієнти купують 1-2 одиниці кожні 7 років. Рішення про покупку залежать від економічної ефективності, довговічності та продуктивності.',
      exampleTwo: 'Вони купують послуги онлайн та надають перевагу моделям підписок.',
    },
    [B2C_NAMES.NEEDS_AND_PROBLEMS]: {
      name: 'Потреби і проблеми',
      instruction:
        'Визначте потреби й проблеми цільової аудиторії. За допомогою маркетингових досліджень з’ясуйте болі та незадоволені потреби, щоб запропонувати рішення, які ефективно їх закривають.',
      exampleOne:
        'Клієнти, які працюють на важких та вологих ґрунтах, потребують надійних та довговічних тракторів, які можуть ефективно працювати в складних умовах, не пошкоджуючи ґрунт.',
      exampleTwo:
        "Стикаються з мовними бар'єрами, які перешкоджають ефективній взаємодії. Цільова аудиторія шукає онлайн-рішення, яке забезпечує швидку та надійну мовну підтримку для легкої орієнтації в різних мовних середовищах.",
    },
  },
  [CONSUMER_NEEDS_NAMES.B2B]: {
    [B2B_NAMES.INDUSTRY]: {
      name: 'Галузь',
      instruction:
        'Визначте галузь вашого ідеального бізнес-клієнта. Оберіть найбільш релевантний сектор або опишіть його, якщо відповідного варіанту немає в списку. Укажіть регіон, де зосереджена основна діяльність клієнтів. Якщо вони працюють у кількох регіонах, укажіть ключовий.',
      exampleOne: 'Сільське господарство',
      exampleTwo: "Багатонаціональні корпорації, технології, охорона здоров'я, право, освіта.",
    },
    [B2B_NAMES.ANNUAL_REVENUE]: {
      name: 'Річний дохід',
      instruction:
        'Укажіть діапазон річного виторгу типового бізнес-клієнта. Визначте його, аналізуючи фінансову звітність і дані про доходи.',
      exampleOne: '80 000 000 USD',
      exampleTwo: '40 000 000 USD',
    },
    [B2B_NAMES.NUMBER_OF_EMPLOYERS]: {
      name: 'Кількість працівників',
      instruction:
        'Укажіть типовий діапазон кількості працівників у компанії клієнта. Врахуйте всі формати зайнятості: повна, часткова та контрактна.',
      exampleOne: '300-900',
      exampleTwo: '250+',
    },
    [B2B_NAMES.REGION]: {
      name: 'Регіон',
      instruction:
        'Укажіть основний географічний регіон або ринок, на якому працює типовий бізнес-клієнт. Якщо він активний у кількох регіонах, наведіть найважливіші.',
      exampleOne: 'США',
      exampleTwo: 'Північна Америка, Європа, Азіатсько-Тихоокеанський регіон',
    },
    [B2B_NAMES.PURCHASING_CYCLE]: {
      name: 'Цикл купівлі',
      instruction:
        'Опишіть модель закупівель вашого бізнес-клієнта, зокрема періодичність і типові строки ухвалення ключових купівельних рішень.',
      exampleOne: 'Кожні 5-7 років',
      exampleTwo: 'Щоквартально/Щорічно',
    },
  },
  [CONSUMER_NEEDS_NAMES.PURCHASING_DECISION]: {
    name: 'Рішення про купівлю',
    instruction:
      'Якщо ваші клієнти — юридичні особи, опишіть структуру ухвалення рішень усередині організації. Вкажіть, хто має повноваження на купівлю, а також роль впливових осіб або стейкхолдерів у процесі.',
    exampleOne:
      'Рішення про купівлю зазвичай приймають власники або вище керівництво. Фахівці з обладнання іноді можуть впливати на процес прийняття рішень, надаючи рекомендації або аналітичні дані.',
    exampleTwo:
      'Рішення про закупівлі зазвичай приймаються керівниками відділів або менеджерами зі закупівель у підприємствах, а також керівниками проектів або адміністраторами в державних установах.',
  },
  [CONSUMER_NEEDS_NAMES.INFLUENCING_FACTORS]: {
    name: 'Фактори впливу',
    instruction:
      'Визначте ключові фактори, що впливають на рішення клієнтів щодо купівлі. Це можуть бути характеристики продукту, ціна, репутація бренду, відгуки або специфічні потреби, які вирішує ваш продукт чи послуга. Надайте цілісний огляд факторів, які клієнти ставлять у пріоритет.',
    exampleOne:
      'Надійність, довговічність, продуктивність та економічна ефективність продукту є ключовими факторами, що впливають на рішення про покупку. Клієнти надають пріоритет функціям, які відповідають їхнім потребам, таким як робота на важких та вологих ґрунтах.',
    exampleTwo:
      'Ключові фактори, що впливають на рішення про покупку, включають якість обслуговування, мовне покриття, легкість інтеграції, репутацію постачальника та гнучкість.',
  },
  [CONSUMER_NEEDS_NAMES.DIFFERENCES]: {
    name: 'Відмінність в поведінці',
    instruction:
      'Опишіть ключові відмінності в поведінці або вподобаннях між різними сегментами клієнтів. Підкресліть різницю в купівельних звичках, очікуваннях від продукту чи реакції на маркетингові активності.',
    exampleOne:
      'Сільськогосподарські компанії надають пріоритет оптовим закупівлям та цінній ефективності, тоді як приватні ферми часто обирають роздріб, надаючи пріоритет надійності. Ця різниця в поведінці покупців впливає на плани виробництва відповідно до попиту.',
    exampleTwo:
      'Бізнес надає пріоритет швидким перекладам, тоді як державні установи зосереджуються на комплексних мовних рішеннях для офіційних документів. Бізнес добре реагує на цільовий маркетинг, наголошуючи на точності та своєчасності, тоді як державні установи цінують довіру та безпеку.',
  },
  [CONSUMER_NEEDS_NAMES.HOW_CUSTOMERS_SATISFIED]: {
    name: 'Задоволеність ринку',
    instruction:
      'Опишіть поточний рівень задоволеності клієнтів на ринку. Розкрийте загальний настрій щодо наявних продуктів і сервісів. Підкресліть аспекти, якими клієнти задоволені, а також ті, де вони очікують покращень: функціонал, якість сервісу, ціни тощо.',
    exampleOne:
      'Задоволеність клієнтів існуючими продуктами різна. Хоча деякі клієнти задоволені функціями та продуктивністю, інші прагнуть покращення довговічності та ціни. Загальний настрій свідчить про поєднання задоволення та потреби в удосконаленнях.',
    exampleTwo:
      'Задоволеність клієнтів існуючими на ринку послугами є помірною. Деякі клієнти висловлюють невдоволення складними процесами реєстрації та доступністю послуг. Спрощення процедур та підвищення зручності можуть покращити загальний рівень задоволеності.',
  },
  [CONSUMER_NEEDS_NAMES.DEMAND]: {
    name: 'Створення попиту',
    instruction:
      'Проаналізуйте, чи працює бізнес на ринку з уже сформованим попитом, чи створює новий попит завдяки інноваційним продуктам або послугам.',
    exampleOne:
      'Компанія працює на ринку зі сформованим попитом. Трактори є важливим сільськогосподарським обладнанням, а постійна потреба в механізованих сільськогосподарських рішеннях стимулює попит. Бізнес може впроваджувати інновації в рамках цього існуючого попиту, але не є піонером на абсолютно новому ринку.',
    exampleTwo:
      'Компанія є піонером нового попиту, впроваджуючи онлайн-платформу для пошуку перекладачів. Цей інноваційний сервіс ще не є усталеним на ринку, пропонуючи унікальне рішення для задоволення потреб клієнтів.',
  },
  [CONSUMER_NEEDS_NAMES.CUSTOMER_BASE_SIZE]: {
    name: 'Розмір клієнтської бази',
    instruction:
      'Надайте огляд поточної клієнтської бази, акцентуючи на кількості клієнтів. Опишіть рівень зацікавленості продуктом, посилаючись на запити, реєстрації, передзамовлення та інші показники залучення.',
    exampleOne:
      'Компанія має понад 800 фермерських господарств серед клієнтів. У 2022 році кількість клієнтів зросла на 12% порівняно з попереднім роком, що свідчить про зростання інтересу та залученості до продукту. Крім того, 40% клієнтів є постійними клієнтами.',
    exampleTwo:
      'Наразі клієнтська база платформи складається з 20 000 зареєстрованих перекладачів з усього світу, зі щомісячним припливом 100 нових реєстрацій. У 2022 році оброблено 50 000 замовлень, що на 23% більше, ніж у попередньому році.',
  },
  [CONSUMER_NEEDS_NAMES.SURVEY]: {
    name: 'Відгуки про продукт',
    instruction:
      'Опишіть ініціативу опитування клієнтів, її мету та охоплення. Визначте цільову аудиторію й методологію збору даних. Коротко перелічіть ключові запитання анкети й підсумуйте основні висновки: тренди, вподобання та настрої потенційних або наявних клієнтів.',
    exampleOne:
      "Ініціатива з опитування клієнтів оцінює рівень задоволеності після продажу. В форматі телефонних інтерв'ю було опитано нещодавніх покупців. Результати показують, що 80% клієнтів висловлюють задоволення та мають намір повторити покупки.",
    exampleTwo:
      'Компанія проводила опитування клієнтів для оцінки їхньої задоволеності. Метою опитування було зібрати відгуки клієнтів щодо покращення послуг за допомогою онлайн-анкет. Результати дослідження висвітлили потребу в більшій кількості варіантів оплати та мобільній версії платформи.',
  },
};

export const MARKET_ANALYSIS_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.MARKET_OVERVIEW]: MARKET_OVERVIEW_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.COMPETITIVE_ANALYSIS]: COMPETITIVE_ANALYSIS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.CONSUMER_NEEDS]: CONSUMER_NEEDS_INSTRUCTIONS,
};
